import type { CurrentUser } from '../api/active/generated/types';
import type { CpqState } from '../pages/cpq-v2/state/types';
import {
  createFormalWorkspaceSessionScope,
  type FormalWorkspaceSessionScope,
} from './formalWorkspaceSession';
import { isFormalNewQuoteId } from './quoteLegacyProjection';

/** A leave warning protects local edits; it never grants a save permission. */
export function hasUnsavedFormalWorkspaceDrafts(
  state: CpqState,
  user: CurrentUser | undefined,
  mountedScope?: FormalWorkspaceSessionScope,
): boolean {
  if (!user) return false;
  if (mountedScope) {
    const current = createFormalWorkspaceSessionScope(user);
    if (
      mountedScope.actorUserId !== current.actorUserId ||
      mountedScope.companyId !== current.companyId ||
      mountedScope.authorizationKey !== current.authorizationKey
    )
      return false;
  }
  const canProtect = (code: 'quote.create' | 'quote.edit') => {
    if (!user.permissions.includes(code)) return false;
    const scopes =
      user.permissionDataScopes === undefined
        ? user.dataScopes
        : user.permissionDataScopes[code];
    // Initial unsaved creation uses the runtime's original actor/department gate.
    // Older sessions need not expose a create data scope; do not lose their draft.
    if (
      code === 'quote.create' &&
      user.permissionDataScopes === undefined &&
      !scopes?.some((scope) => scope.resourceType === 'quote')
    )
      return Boolean(user.primaryDepartmentId);
    return (
      scopes?.some(
        (scope) => scope.resourceType === 'quote' && scope.scopeType !== 'none',
      ) ?? false
    );
  };
  return state.openQuoteIds.some((id) => {
    const quote = state.quotes[id];
    if (
      !quote?.dirty ||
      quote.status !== 'draft' ||
      quote.caseRehearsalSnapshot
    )
      return false;
    if (isFormalNewQuoteId(id))
      return canProtect('quote.create') || canProtect('quote.edit');
    // Only a server-loaded formal version can be an existing editable draft.
    // No owner facts are invented for narrower scopes: retain its leave warning.
    return (
      /^[1-9][0-9]{0,18}$/.test(id) &&
      Boolean(quote.formalVersionId) &&
      canProtect('quote.edit')
    );
  });
}
